import React, { useEffect, useState } from 'react';
import { importAPI } from '../api';
import { toast } from 'sonner';
import { Plus, Pencil, Check, X, PlugZap, Copy } from 'lucide-react';

// Google Sheet sources. The service-account key never leaves the backend — only its e-mail is shown
// so the employee can give that address Viewer access on a Restricted Sheet.
export const SheetSourceMaster = () => {
  const [rows, setRows] = useState([]);
  const [sa, setSa] = useState({ configured: false, email: null });
  const [form, setForm] = useState({ name: '', url: '', tab_range: '' });
  const [editing, setEditing] = useState(null);
  const [edit, setEdit] = useState({ name: '', tab_range: '' });
  const [testing, setTesting] = useState(null);

  const load = () => importAPI.sheetSourcesAll().then((r) => setRows(r.data || [])).catch(() => toast.error('Failed to load Sheet sources'));
  useEffect(() => {
    load();
    importAPI.serviceAccount().then((r) => setSa(r.data || {})).catch(() => {});
  }, []);

  const add = async () => {
    if (!form.name.trim() || !form.url.trim()) { toast.error('Sheet name and link are required'); return; }
    try { await importAPI.createSheetSource(form); setForm({ name: '', url: '', tab_range: '' }); toast.success('Sheet source added'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not add Sheet source'); }
  };

  const save = async (id) => {
    try { await importAPI.updateSheetSource(id, edit); setEditing(null); toast.success('Sheet source updated'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update Sheet source'); }
  };

  const toggle = async (row) => {
    try { await importAPI.updateSheetSource(row.id, { is_active: !row.is_active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update Sheet source'); }
  };

  const test = async (row) => {
    setTesting(row.id);
    try {
      const r = await importAPI.testSheetSource(row.id);
      if (r.data.ok) toast.success(`Connected via ${r.data.access_mode} · ${r.data.total_rows} row(s), ${r.data.columns.length} column(s)`);
      else toast.error(r.data.detail || 'Connection failed');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Connection failed'); }
    finally { setTesting(null); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="space-y-4" data-testid="settings-sheet-sources-tab">
      <div>
        <h2 className="text-xl font-outfit font-medium">Google Sheet Sources</h2>
        <p className="text-sm text-muted-foreground">Keep each Sheet Restricted. Share it with the GNONS service account as Viewer — employees keep normal Editor access and never need CRM credentials.</p>
      </div>

      <div className={`rounded-lg border px-3 py-2 text-xs ${sa.configured ? 'border-green-200 bg-green-50 text-green-800 dark:bg-green-900/20' : 'border-amber-200 bg-amber-50 text-amber-800 dark:bg-amber-900/20'}`}
        data-testid="sheet-service-account">
        {sa.configured ? (
          <span className="flex flex-wrap items-center gap-2">
            Share each private Sheet with:
            <code className="rounded bg-white px-1.5 py-0.5 font-mono dark:bg-slate-900">{sa.email}</code>
            <button onClick={() => { navigator.clipboard?.writeText(sa.email); toast.success('Copied'); }}
              data-testid="sheet-sa-copy" className="inline-flex items-center gap-1 underline"><Copy className="h-3 w-3" /> copy</button>
            (read-only access)
          </span>
        ) : 'Private Sheet access is not configured yet — add the GOOGLE_SA_JSON secret in Manage Publishing. Until then only link-shared Sheets can be pulled.'}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
        <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Sheet name (e.g. Employee A Leads)"
          data-testid="sheet-new-name" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
        <input value={form.url} onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))} placeholder="https://docs.google.com/spreadsheets/d/..."
          data-testid="sheet-new-url" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900 sm:col-span-2" />
        <div className="flex gap-2">
          <input value={form.tab_range} onChange={(e) => setForm((f) => ({ ...f, tab_range: e.target.value }))} placeholder="Tab/Range (optional)"
            data-testid="sheet-new-range" className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          <button onClick={add} data-testid="sheet-add-btn" className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white">
            <Plus className="h-4 w-4" /> Add
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-muted-foreground dark:bg-slate-800">
            <tr><th className="px-3 py-2">Sheet</th><th className="px-3 py-2">Tab/Range</th><th className="px-3 py-2">Rows imported</th>
              <th className="px-3 py-2">Last pull</th><th className="px-3 py-2">Last success</th><th className="px-3 py-2">By</th>
              <th className="px-3 py-2 text-center">Active</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`sheet-row-${r.id}`}>
                <td className="px-3 py-2">
                  {editing === r.id
                    ? <input value={edit.name} onChange={(e) => setEdit((x) => ({ ...x, name: e.target.value }))}
                        data-testid={`sheet-edit-name-${r.id}`} className="w-full rounded border border-slate-200 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900" />
                    : <><div className="font-medium">{r.name}</div><div className="font-mono text-[11px] text-muted-foreground">{r.key}</div></>}
                </td>
                <td className="px-3 py-2 text-xs">
                  {editing === r.id
                    ? <input value={edit.tab_range} onChange={(e) => setEdit((x) => ({ ...x, tab_range: e.target.value }))}
                        data-testid={`sheet-edit-range-${r.id}`} className="w-full rounded border border-slate-200 px-2 py-1 text-sm dark:border-slate-700 dark:bg-slate-900" />
                    : (r.tab_range || '—')}
                </td>
                <td className="px-3 py-2">{r.imported_rows}</td>
                <td className="px-3 py-2 text-xs">{fmt(r.last_pull_at)}</td>
                <td className="px-3 py-2 text-xs">{fmt(r.last_success_at)}</td>
                <td className="px-3 py-2 text-xs">{r.updated_by_name || r.created_by_name || '—'}</td>
                <td className="px-3 py-2 text-center">
                  <input type="checkbox" checked={r.is_active} onChange={() => toggle(r)} data-testid={`sheet-active-${r.id}`} />
                </td>
                <td className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-2">
                    <button onClick={() => test(r)} disabled={testing === r.id} data-testid={`sheet-test-${r.id}`}
                      className="inline-flex items-center gap-1 text-xs text-primary disabled:opacity-50">
                      <PlugZap className="h-3.5 w-3.5" /> {testing === r.id ? 'Testing…' : 'Test'}
                    </button>
                    {editing === r.id ? (
                      <>
                        <button onClick={() => save(r.id)} data-testid={`sheet-save-${r.id}`}><Check className="h-4 w-4 text-green-600" /></button>
                        <button onClick={() => setEditing(null)} data-testid={`sheet-cancel-${r.id}`}><X className="h-4 w-4 text-slate-400" /></button>
                      </>
                    ) : (
                      <button onClick={() => { setEditing(r.id); setEdit({ name: r.name, tab_range: r.tab_range || '' }); }}
                        data-testid={`sheet-edit-${r.id}`}><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                    )}
                  </span>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-3 text-sm text-muted-foreground">No Sheet sources configured yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SheetSourceMaster;
